import React, { useMemo, useCallback } from 'react';
import { find, uniq, map, findIndex, filter } from 'lodash';
import { FormikProps } from 'formik';
import { ArrowBackIosRounded, ArrowForwardIosRounded, CachedRounded } from '@mui/icons-material';
import { extraColor } from '@styles/muiTheme';
import {
  IButtonConfig,
  TAnyItem,
  IMenuItem,
  IChildMenuItem,
  EMenuItemType,
  IMenuValues,
  TNullableAnyItem,
} from '../types';
import { getNewItem, getMenuItemColors, getExpandedIds } from '../helpers';

interface IConfigProps extends Pick<FormikProps<IMenuValues>, 'isValid' | 'dirty' | 'isSubmitting'> {
  expanded: string[];
  selectedMenuItem: TNullableAnyItem;
  menuItemsValues: IMenuItem[];
  disabledIds?: string[];
  setSelectedMenuItem: (item: TNullableAnyItem) => void;
  setExpanded: (expanded: string[]) => void;
  setMenuItemsValues: (menuItems: IMenuItem[]) => void;
}

interface IConfigData {
  buttonsConfig: IButtonConfig[];
}

export const useButtonsConfig = ({
  isValid,
  dirty,
  isSubmitting,
  expanded,
  selectedMenuItem,
  disabledIds,
  menuItemsValues,
  setMenuItemsValues,
  setSelectedMenuItem,
  setExpanded,
}: IConfigProps): IConfigData => {
  const onAddMenu = useCallback((): void => {
    const menuItem: IMenuItem = {
      ...getNewItem(menuItemsValues.length),
      parentId: null,
      childMenuItems: [],
    };

    setMenuItemsValues([...menuItemsValues, menuItem]);
    setMenuItemsValues([...menuItemsValues, menuItem]);
  }, [menuItemsValues, setMenuItemsValues]);

  const onAddChildMenu = useCallback((): void => {
    if (!selectedMenuItem || !('childMenuItems' in selectedMenuItem)) {
      return;
    }

    const { childMenuItems, id: parentId } = selectedMenuItem;

    const childMenuItem: IChildMenuItem = {
      ...getNewItem(childMenuItems.length, parentId),
      parentId,
    };

    const parentMenuItem: IMenuItem = {
      ...selectedMenuItem,
      menuItemType: EMenuItemType.Expandable,
      childMenuItems: [...childMenuItems, childMenuItem],
    };

    setExpanded(uniq<string>([...expanded, parentMenuItem.id]));
    setMenuItemsValues(
      map<IMenuItem, IMenuItem>(menuItemsValues, (menuItem) =>
        menuItem.id === parentId
          ? {
              ...parentMenuItem,
              ...getMenuItemColors(parentMenuItem),
            }
          : menuItem,
      ),
    );
  }, [selectedMenuItem, expanded, menuItemsValues, setMenuItemsValues, setExpanded]);

  const onCollapseAll = useCallback((): void => setExpanded([]), [setExpanded]);

  const onExpandAll = useCallback(
    (): void => setExpanded(getExpandedIds(menuItemsValues)),
    [setExpanded, menuItemsValues],
  );

  const onPrevNextClick = useCallback(
    (isNext: boolean) => {
      if (!selectedMenuItem) {
        return;
      }
      
      const supplement: number = isNext ? 1 : -1;
      const { parentId, id: currentId } = selectedMenuItem;
      
      const parentArr: TAnyItem[] = !parentId ? menuItemsValues : find(menuItemsValues, ['id', parentId])?.childMenuItems ?? [];
      const currentIdx: number = findIndex<TAnyItem>(parentArr, ['id', currentId]);
      
      let nextIdx: number = currentIdx + supplement;

      while (nextIdx >= 0 && nextIdx < parentArr.length) {
        const nextId = parentArr[nextIdx]?.id;
        if (!disabledIds?.includes(nextId)) {
          setSelectedMenuItem({
            ...parentArr[nextIdx],
            parentId,
          } as TAnyItem);
          break;
        }
        nextIdx += supplement;
      }
    },
    [menuItemsValues, selectedMenuItem, setSelectedMenuItem, disabledIds]
  );

  const onDeleteMenu = useCallback(() => {
    if (!selectedMenuItem) {
      return;
    }

    const { id, parentId } = selectedMenuItem;

    const updatedMenuItems: IMenuItem[] = parentId
      ? map<IMenuItem, IMenuItem>(menuItemsValues, (menuItem) =>
          menuItem.id === parentId
            ? {
                ...menuItem,
                childMenuItems: filter<IChildMenuItem>(menuItem.childMenuItems, ({ id: childId }) => childId !== id),
              }
            : menuItem,
        )
      : filter<IMenuItem>(menuItemsValues, ({ id: itemId }) => itemId !== id);

    setMenuItemsValues(updatedMenuItems);
    setSelectedMenuItem(null);
  }, [selectedMenuItem, menuItemsValues, setMenuItemsValues, setSelectedMenuItem]);

  const buttonsConfig = useMemo<IButtonConfig[]>(
    () => [
      {
        color: 'success',
        buttons: [
          {
            children: 'Save',
            type: 'submit',
            disabled: !isValid || !dirty || isSubmitting,
            sx: {
              opacity: 0.97,
              backgroundColor: extraColor.palette.green.main,
              ':hover': {
                backgroundColor: extraColor.palette.green.main,
                opacity: 1,
              }
            },
          },
        ],
      },
      {
        buttons: [
          {
            children: 'Add Menu',
            onClick: onAddMenu,
          },
          {
            children: 'Add Child Menu',
            onClick: onAddChildMenu,
            disabled: !selectedMenuItem || !!selectedMenuItem?.parentId,
          },
        ],
      },
      {
        buttons: [
          {
            children: 'Expand All',
            onClick: onExpandAll,
          },
          {
            children: 'Collapse All',
            onClick: onCollapseAll,
          },
        ],
      },
      {
        buttons: [
          {
            children: <ArrowBackIosRounded />,
            onClick: () => onPrevNextClick(false),
            disabled: !selectedMenuItem,
          },
          {
            children: <ArrowForwardIosRounded />,
            onClick: () => onPrevNextClick(true),
            disabled: !selectedMenuItem,
          },
        ],
      },
      {
        buttons: [
          {
            children: <CachedRounded />,
            onClick: () => {
              window.location.reload();
            },
          },
        ],
      },
      {
        color: 'error',
        buttons: [
          {
            children: 'Delete Menu',
            onClick: onDeleteMenu,
            disabled: !selectedMenuItem,
          },
        ],
      },
    ],
    [
      isValid,
      dirty,
      isSubmitting,
      selectedMenuItem,
      onAddMenu,
      onAddChildMenu,
      onExpandAll,
      onCollapseAll,
      onPrevNextClick,
      onDeleteMenu,
    ],
  );

  return { buttonsConfig };
};
